import {
    motionGroups,
    type MotionTokenSample,
} from "./designTokensMotionCatalog";

export type TokenSample =
    | MotionTokenSample
    | "color"
    | "opacity"
    | "spacing"
    | "border"
    | "radius"
    | "shadow"
    | "fontFamily"
    | "fontSize"
    | "lineHeight"
    | "fontWeight"
    | "tracking";

export interface CssTokenEntry {
    name: `--${string}`;
    sample: TokenSample;
    isDeviation?: true;
    /** Code-only token with no same-value counterpart among Figma's variables. */
    isNotInFigma?: true;
    /** Primitive token this semantic token resolves to, when it has one. */
    sourceToken?: `--color-${string}`;
    /** What the token is for, when the catalog lists it in a Use column. */
    usage?: string;
}

export interface CssTokenGroup {
    label: string;
    tokens: readonly CssTokenEntry[];
}

export type FontTokenName = `--font-${string}` | `--tracking-${string}`;

export interface TypographyMixinEntry {
    name: `text-${string}`;
    copyValue: `@include tokens.${string};`;
    /** Class key in DesignTokensPlayground.module.scss rendering the sample. */
    sampleClass: string;
    /** Effective font tokens the recipe resolves to, after mixin inheritance. */
    sourceTokens: readonly FontTokenName[];
    /**
     * Declarations the recipe sets literally, with no token behind them.
     * Multiple literals are joined with "; " in mixin declaration order.
     */
    recipeDetail?: string;
    /** Recipe with no exact-value counterpart among Figma's text styles. */
    isNotInFigma?: true;
}

export interface TypographyMixinGroup {
    label: string;
    mixins: readonly TypographyMixinEntry[];
}

export type DesignTokenTabId =
    | "primitive-colors"
    | "semantic-colors"
    | "spacing"
    | "border-widths"
    | "radius"
    | "shadows"
    | "motion"
    | "font"
    | "typography";

interface CssTokenTab {
    id: Exclude<DesignTokenTabId, "typography">;
    label: string;
    groups: readonly CssTokenGroup[];
}

interface TypographyTab {
    id: "typography";
    label: "Typography";
    typographyGroups: readonly TypographyMixinGroup[];
}

export type DesignTokenTab = CssTokenTab | TypographyTab;

const primitiveColorGroups = [
    {
        label: "Base",
        tokens: [
            { name: "--color-white", sample: "color" },
            // Figma's palette has no pure black (darkest is Midnight gray/950).
            { name: "--color-black", sample: "color", isNotInFigma: true },
        ],
    },
    {
        label: "Midnight gray",
        tokens: [
            { name: "--color-midnight-gray-50", sample: "color" },
            { name: "--color-midnight-gray-100", sample: "color" },
            { name: "--color-midnight-gray-200", sample: "color" },
            { name: "--color-midnight-gray-300", sample: "color" },
            { name: "--color-midnight-gray-400", sample: "color" },
            { name: "--color-midnight-gray-500", sample: "color" },
            { name: "--color-midnight-gray-600", sample: "color" },
            { name: "--color-midnight-gray-700", sample: "color" },
            { name: "--color-midnight-gray-800", sample: "color" },
            { name: "--color-midnight-gray-900", sample: "color" },
            { name: "--color-midnight-gray-950", sample: "color" },
        ],
    },
    {
        label: "Sunset pink",
        tokens: [
            { name: "--color-sunset-pink-50", sample: "color" },
            { name: "--color-sunset-pink-100", sample: "color" },
            { name: "--color-sunset-pink-200", sample: "color" },
            { name: "--color-sunset-pink-300", sample: "color" },
            { name: "--color-sunset-pink-400", sample: "color" },
            { name: "--color-sunset-pink-500", sample: "color" },
            { name: "--color-sunset-pink-600", sample: "color" },
            { name: "--color-sunset-pink-700", sample: "color" },
            { name: "--color-sunset-pink-800", sample: "color" },
            { name: "--color-sunset-pink-900", sample: "color" },
            { name: "--color-sunset-pink-950", sample: "color" },
        ],
    },
    {
        label: "Red (legacy)",
        tokens: [
            { name: "--color-red-50", sample: "color", isNotInFigma: true },
            { name: "--color-red-100", sample: "color", isNotInFigma: true },
            { name: "--color-red-200", sample: "color", isNotInFigma: true },
            { name: "--color-red-300", sample: "color", isNotInFigma: true },
            { name: "--color-red-400", sample: "color", isNotInFigma: true },
            { name: "--color-red-500", sample: "color", isNotInFigma: true },
            { name: "--color-red-600", sample: "color", isNotInFigma: true },
            { name: "--color-red-700", sample: "color", isNotInFigma: true },
            { name: "--color-red-800", sample: "color", isNotInFigma: true },
            { name: "--color-red-900", sample: "color", isNotInFigma: true },
        ],
    },
    {
        label: "Yellow (legacy)",
        tokens: [
            { name: "--color-yellow-50", sample: "color", isNotInFigma: true },
            { name: "--color-yellow-100", sample: "color", isNotInFigma: true },
            { name: "--color-yellow-200", sample: "color", isNotInFigma: true },
            { name: "--color-yellow-300", sample: "color", isNotInFigma: true },
            { name: "--color-yellow-400", sample: "color", isNotInFigma: true },
            { name: "--color-yellow-500", sample: "color", isNotInFigma: true },
            { name: "--color-yellow-600", sample: "color", isNotInFigma: true },
            { name: "--color-yellow-700", sample: "color", isNotInFigma: true },
            { name: "--color-yellow-800", sample: "color", isNotInFigma: true },
            { name: "--color-yellow-900", sample: "color", isNotInFigma: true },
        ],
    },
    {
        label: "Sea green",
        tokens: [
            { name: "--color-sea-green-50", sample: "color" },
            { name: "--color-sea-green-100", sample: "color" },
            { name: "--color-sea-green-200", sample: "color" },
            { name: "--color-sea-green-300", sample: "color" },
            { name: "--color-sea-green-400", sample: "color" },
            { name: "--color-sea-green-500", sample: "color" },
            { name: "--color-sea-green-600", sample: "color" },
            { name: "--color-sea-green-700", sample: "color" },
            { name: "--color-sea-green-800", sample: "color" },
            { name: "--color-sea-green-900", sample: "color" },
            { name: "--color-sea-green-950", sample: "color" },
        ],
    },
    {
        label: "Ocean blue",
        tokens: [
            { name: "--color-ocean-blue-50", sample: "color" },
            { name: "--color-ocean-blue-100", sample: "color" },
            { name: "--color-ocean-blue-200", sample: "color" },
            { name: "--color-ocean-blue-300", sample: "color" },
            { name: "--color-ocean-blue-400", sample: "color" },
            { name: "--color-ocean-blue-500", sample: "color" },
            { name: "--color-ocean-blue-600", sample: "color" },
            { name: "--color-ocean-blue-700", sample: "color" },
            { name: "--color-ocean-blue-800", sample: "color" },
            { name: "--color-ocean-blue-900", sample: "color" },
            { name: "--color-ocean-blue-950", sample: "color" },
        ],
    },
    {
        label: "Dusk purple",
        tokens: [
            { name: "--color-dusk-purple-50", sample: "color" },
            { name: "--color-dusk-purple-100", sample: "color" },
            { name: "--color-dusk-purple-200", sample: "color" },
            { name: "--color-dusk-purple-300", sample: "color" },
            { name: "--color-dusk-purple-400", sample: "color" },
            { name: "--color-dusk-purple-500", sample: "color" },
            { name: "--color-dusk-purple-600", sample: "color" },
            { name: "--color-dusk-purple-700", sample: "color" },
            { name: "--color-dusk-purple-800", sample: "color" },
            { name: "--color-dusk-purple-900", sample: "color" },
            { name: "--color-dusk-purple-950", sample: "color" },
        ],
    },
    {
        label: "Alpha",
        tokens: [
            { name: "--color-alpha-50", sample: "color" },
            { name: "--color-alpha-100", sample: "color" },
            { name: "--color-alpha-200", sample: "color" },
            { name: "--color-alpha-300", sample: "color" },
            { name: "--color-white-alpha-50", sample: "color" },
            { name: "--color-white-alpha-100", sample: "color" },
            { name: "--color-white-alpha-200", sample: "color" },
            { name: "--color-white-alpha-300", sample: "color" },
            // Figma's Alpha white ramp stops at 300 (18%); no 50% step.
            {
                name: "--color-white-alpha-500",
                sample: "color",
                isNotInFigma: true,
            },
        ],
    },
] satisfies readonly CssTokenGroup[];

const semanticColorGroups = [
    {
        label: "Foreground",
        tokens: [
            {
                name: "--fg-primary",
                sample: "color",
                sourceToken: "--color-midnight-gray-900",
            },
            {
                name: "--fg-secondary",
                sample: "color",
                sourceToken: "--color-midnight-gray-700",
            },
            {
                name: "--fg-tertiary",
                sample: "color",
                sourceToken: "--color-midnight-gray-500",
            },
            {
                name: "--fg-disabled",
                sample: "color",
                sourceToken: "--color-midnight-gray-400",
            },
            {
                name: "--fg-on-solid",
                sample: "color",
                sourceToken: "--color-white",
            },
            {
                name: "--fg-accent",
                sample: "color",
                sourceToken: "--color-ocean-blue-600",
            },
            {
                name: "--fg-accent-pressed",
                sample: "color",
                sourceToken: "--color-ocean-blue-700",
            },
            {
                name: "--fg-accent-placeholder",
                sample: "color",
                sourceToken: "--color-ocean-blue-500",
            },
            {
                name: "--fg-accent-disabled",
                sample: "color",
                sourceToken: "--color-ocean-blue-400",
            },
            {
                name: "--fg-error",
                sample: "color",
                sourceToken: "--color-sunset-pink-600",
            },
            {
                name: "--fg-error-pressed",
                sample: "color",
                sourceToken: "--color-sunset-pink-700",
            },
            {
                name: "--fg-error-placeholder",
                sample: "color",
                sourceToken: "--color-sunset-pink-500",
            },
            {
                name: "--fg-error-disabled",
                sample: "color",
                sourceToken: "--color-sunset-pink-400",
            },
            {
                name: "--fg-warning",
                sample: "color",
                sourceToken: "--color-yellow-600",
                isNotInFigma: true,
            },
            {
                name: "--fg-success",
                sample: "color",
                sourceToken: "--color-sea-green-600",
                isNotInFigma: true,
            },
            {
                name: "--fg-on-inverse",
                sample: "color",
                sourceToken: "--color-white",
                isNotInFigma: true,
            },
            {
                name: "--fg-on-inverse-disabled",
                sample: "color",
                sourceToken: "--color-midnight-gray-500",
                isNotInFigma: true,
            },
        ],
    },
    {
        label: "Background surfaces",
        tokens: [
            {
                name: "--bg-primary",
                sample: "color",
                sourceToken: "--color-white",
            },
            {
                name: "--bg-secondary",
                sample: "color",
                sourceToken: "--color-midnight-gray-50",
            },
            {
                name: "--bg-tertiary",
                sample: "color",
                sourceToken: "--color-midnight-gray-100",
            },
            {
                name: "--bg-disabled",
                sample: "color",
                sourceToken: "--color-midnight-gray-200",
            },
        ],
    },
    {
        label: "Neutral fills",
        tokens: [
            {
                name: "--bg-neutral",
                sample: "color",
                sourceToken: "--color-alpha-100",
            },
            {
                name: "--bg-neutral-hover",
                sample: "color",
                sourceToken: "--color-alpha-100",
            },
            {
                name: "--bg-neutral-pressed",
                sample: "color",
                sourceToken: "--color-alpha-200",
            },
            {
                name: "--bg-neutral-hover-on-fill",
                sample: "color",
                sourceToken: "--color-alpha-200",
            },
            {
                name: "--bg-neutral-subtle-hover",
                sample: "color",
                sourceToken: "--color-alpha-50",
            },
            {
                name: "--bg-neutral-strong",
                sample: "color",
                sourceToken: "--color-midnight-gray-300",
            },
        ],
    },
    {
        label: "Accent fills",
        tokens: [
            {
                name: "--bg-accent",
                sample: "color",
                sourceToken: "--color-ocean-blue-600",
            },
            {
                name: "--bg-accent-hover",
                sample: "color",
                sourceToken: "--color-ocean-blue-700",
            },
            {
                name: "--bg-accent-pressed",
                sample: "color",
                sourceToken: "--color-ocean-blue-800",
            },
            {
                name: "--bg-accent-outline-pressed",
                sample: "color",
                sourceToken: "--color-ocean-blue-50",
            },
            {
                name: "--bg-accent-selected",
                sample: "color",
                sourceToken: "--color-ocean-blue-50",
            },
            {
                name: "--bg-accent-subtle",
                sample: "color",
                sourceToken: "--color-ocean-blue-100",
            },
            {
                name: "--bg-accent-subtle-hover",
                sample: "color",
                sourceToken: "--color-ocean-blue-200",
            },
        ],
    },
    {
        label: "Error fills",
        tokens: [
            {
                name: "--bg-error-outline-pressed",
                sample: "color",
                sourceToken: "--color-sunset-pink-50",
            },
            {
                name: "--bg-error-subtle",
                sample: "color",
                sourceToken: "--color-sunset-pink-100",
            },
            {
                name: "--bg-error-subtle-hover",
                sample: "color",
                sourceToken: "--color-sunset-pink-200",
            },
            {
                name: "--bg-error-selected",
                sample: "color",
                sourceToken: "--color-sunset-pink-50",
            },
        ],
    },
    {
        label: "Code-only backgrounds",
        tokens: [
            {
                name: "--bg-hover-subtle",
                sample: "color",
                sourceToken: "--color-midnight-gray-50",
                isNotInFigma: true,
            },
            {
                name: "--bg-warning-subtle",
                sample: "color",
                sourceToken: "--color-yellow-50",
                isNotInFigma: true,
            },
            {
                name: "--bg-translucent",
                sample: "color",
                sourceToken: "--color-white-alpha-500",
                isNotInFigma: true,
            },
            {
                name: "--bg-inverse",
                sample: "color",
                sourceToken: "--color-midnight-gray-900",
                isNotInFigma: true,
            },
            {
                name: "--bg-inverse-hover",
                sample: "color",
                sourceToken: "--color-midnight-gray-800",
                isNotInFigma: true,
            },
            {
                name: "--bg-inverse-pressed",
                sample: "color",
                sourceToken: "--color-midnight-gray-700",
                isNotInFigma: true,
            },
            {
                name: "--bg-inverse-disabled",
                sample: "color",
                sourceToken: "--color-midnight-gray-300",
                isNotInFigma: true,
            },
        ],
    },
    {
        label: "Opacity",
        tokens: [
            {
                name: "--opacity-disabled",
                sample: "opacity",
                isNotInFigma: true,
            },
        ],
    },
    {
        label: "Borders",
        tokens: [
            {
                name: "--border-default",
                sample: "color",
                sourceToken: "--color-alpha-300",
            },
            {
                name: "--border-hover",
                sample: "color",
                sourceToken: "--color-midnight-gray-400",
            },
            {
                name: "--border-divider",
                sample: "color",
                sourceToken: "--color-alpha-200",
            },
            {
                name: "--border-disabled",
                sample: "color",
                sourceToken: "--color-alpha-200",
            },
            {
                name: "--border-accent",
                sample: "color",
                sourceToken: "--color-ocean-blue-300",
            },
            {
                name: "--border-accent-hover",
                sample: "color",
                sourceToken: "--color-ocean-blue-400",
            },
            {
                name: "--border-accent-disabled",
                sample: "color",
                sourceToken: "--color-ocean-blue-200",
            },
            {
                name: "--border-error",
                sample: "color",
                sourceToken: "--color-sunset-pink-300",
            },
            {
                name: "--border-error-hover",
                sample: "color",
                sourceToken: "--color-sunset-pink-400",
            },
            {
                name: "--border-error-disabled",
                sample: "color",
                sourceToken: "--color-sunset-pink-200",
            },
            {
                name: "--border-invalid",
                sample: "color",
                sourceToken: "--color-red-400",
                isNotInFigma: true,
            },
        ],
    },
    {
        label: "Focus",
        tokens: [
            {
                name: "--border-accent-focus",
                sample: "color",
                sourceToken: "--color-ocean-blue-400",
            },
            {
                name: "--focus-ring-color",
                sample: "color",
                sourceToken: "--color-ocean-blue-100",
                isNotInFigma: true,
            },
        ],
    },
    {
        label: "Charts",
        tokens: [
            {
                name: "--chart-series-kick",
                sample: "color",
                sourceToken: "--color-midnight-gray-300",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-people",
                sample: "color",
                sourceToken: "--color-ocean-blue-300",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-integrations",
                sample: "color",
                sourceToken: "--color-dusk-purple-300",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-rules",
                sample: "color",
                sourceToken: "--color-sunset-pink-300",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-kick-accent",
                sample: "color",
                sourceToken: "--color-midnight-gray-500",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-people-accent",
                sample: "color",
                sourceToken: "--color-ocean-blue-500",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-integrations-accent",
                sample: "color",
                sourceToken: "--color-dusk-purple-500",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-rules-accent",
                sample: "color",
                sourceToken: "--color-sunset-pink-500",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-kick-selected",
                sample: "color",
                sourceToken: "--color-midnight-gray-400",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-people-selected",
                sample: "color",
                sourceToken: "--color-ocean-blue-400",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-integrations-selected",
                sample: "color",
                sourceToken: "--color-dusk-purple-400",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-rules-selected",
                sample: "color",
                sourceToken: "--color-sunset-pink-400",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-kick-hover",
                sample: "color",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-people-hover",
                sample: "color",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-integrations-hover",
                sample: "color",
                isNotInFigma: true,
            },
            {
                name: "--chart-series-rules-hover",
                sample: "color",
                isNotInFigma: true,
            },
            {
                name: "--chart-floor",
                sample: "color",
                sourceToken: "--color-midnight-gray-50",
                isNotInFigma: true,
            },
            {
                name: "--chart-floor-hover",
                sample: "color",
                sourceToken: "--color-midnight-gray-100",
                isNotInFigma: true,
            },
            {
                name: "--chart-window",
                sample: "color",
                sourceToken: "--color-ocean-blue-500",
                isNotInFigma: true,
            },
        ],
    },
] satisfies readonly CssTokenGroup[];

// The full Figma Primitive "Spacing" scale, mirrored by _space.scss.
const spacingScaleSteps = [
    0, 1, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 44, 48, 52,
    56, 60, 64, 68, 72, 76, 80, 84, 88, 92, 96, 100, 120, 140, 160, 180, 200,
    220, 240, 260, 280, 300,
];

const spacingGroups = [
    {
        label: "Scale",
        tokens: spacingScaleSteps.map((step): CssTokenEntry => ({
            name: `--space-${step}`,
            sample: "spacing",
        })),
    },
    {
        label: "Deviations",
        tokens: [
            {
                name: "--space-3",
                sample: "spacing",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--space-5",
                sample: "spacing",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--space-5-5",
                sample: "spacing",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--space-7",
                sample: "spacing",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--space-9",
                sample: "spacing",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--space-11",
                sample: "spacing",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--space-13",
                sample: "spacing",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--space-15",
                sample: "spacing",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--space-22",
                sample: "spacing",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--space-26",
                sample: "spacing",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--space-30",
                sample: "spacing",
                isDeviation: true,
                isNotInFigma: true,
            },
        ],
    },
] satisfies readonly CssTokenGroup[];

// Figma has no border-width variables (stroke weights are raw values there),
// so the whole family is code-only.
const borderWidthGroups = [
    {
        label: "Scale",
        tokens: [
            { name: "--border-width", sample: "border", isNotInFigma: true },
            {
                name: "--border-width-thick",
                sample: "border",
                isNotInFigma: true,
            },
        ],
    },
    {
        label: "Deviations",
        tokens: [
            {
                name: "--border-width-4",
                sample: "border",
                isDeviation: true,
                isNotInFigma: true,
            },
        ],
    },
] satisfies readonly CssTokenGroup[];

const radiusGroups = [
    {
        label: "Scale",
        tokens: [
            { name: "--radius-2xs", sample: "radius" },
            { name: "--radius-xs", sample: "radius" },
            { name: "--radius-s", sample: "radius" },
            { name: "--radius-m", sample: "radius" },
            { name: "--radius-l", sample: "radius" },
            { name: "--radius-xl", sample: "radius" },
            { name: "--radius-2xl", sample: "radius" },
            { name: "--radius-3xl", sample: "radius" },
            { name: "--radius-4xl", sample: "radius" },
            { name: "--radius-5xl", sample: "radius" },
            { name: "--radius-full", sample: "radius", isNotInFigma: true },
        ],
    },
    {
        label: "Deviations",
        tokens: [
            {
                name: "--radius-3",
                sample: "radius",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--radius-5",
                sample: "radius",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--radius-10",
                sample: "radius",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--radius-11",
                sample: "radius",
                isDeviation: true,
                isNotInFigma: true,
            },
        ],
    },
] satisfies readonly CssTokenGroup[];

// Figma effect styles: "Shadow-1", "Shadow-2", "Shadow-3" (and the
// shadow-2-background-blur mixin: --shadow-2 plus backdrop-filter blur(10px),
// Figma's blur radius 20; the catalog cannot sample it as a single custom
// property); the rest are code-only roles.
const shadowGroups = [
    {
        label: "Scale",
        tokens: [
            { name: "--shadow-1", sample: "shadow" },
            { name: "--shadow-hairline", sample: "shadow", isNotInFigma: true },
            { name: "--shadow-low", sample: "shadow", isNotInFigma: true },
            { name: "--shadow-2", sample: "shadow" },
            { name: "--shadow-3", sample: "shadow" },
            { name: "--shadow-overlay", sample: "shadow", isNotInFigma: true },
            {
                name: "--shadow-focus-ring",
                sample: "shadow",
                isNotInFigma: true,
            },
            {
                name: "--shadow-inset-border",
                sample: "shadow",
                isNotInFigma: true,
            },
        ],
    },
    {
        label: "Deviations",
        tokens: [
            {
                name: "--shadow-low-lifted",
                sample: "shadow",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--shadow-float",
                sample: "shadow",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--shadow-preview",
                sample: "shadow",
                isDeviation: true,
                isNotInFigma: true,
            },
        ],
    },
] satisfies readonly CssTokenGroup[];

interface TextScaleStep {
    size: string;
    px: number;
    /** Letter-spacing in em; Figma stores it as a percent of font size. */
    tracking: string;
}

// The Figma font size/height ramp; tracking mirrors $text-tracking in
// styles/tokens/_text-styles.scss.
const textScaleSteps = [
    { size: "3xs", px: 8, tracking: "0" },
    { size: "2xs", px: 10, tracking: "0" },
    { size: "xs", px: 12, tracking: "0" },
    { size: "s", px: 14, tracking: "-0.005em" },
    { size: "m", px: 16, tracking: "-0.01em" },
    { size: "l", px: 18, tracking: "-0.012em" },
    { size: "xl", px: 20, tracking: "-0.015em" },
    { size: "2xl", px: 24, tracking: "-0.018em" },
    { size: "3xl", px: 28, tracking: "-0.02em" },
    { size: "4xl", px: 32, tracking: "-0.025em" },
    { size: "5xl", px: 42, tracking: "-0.03em" },
    { size: "6xl", px: 52, tracking: "-0.03em" },
    { size: "7xl", px: 62, tracking: "-0.03em" },
    { size: "8xl", px: 72, tracking: "-0.035em" },
    { size: "9xl", px: 92, tracking: "-0.04em" },
] satisfies readonly TextScaleStep[];

const textWeights = [
    { label: "regular", weight: 400 },
    { label: "medium", weight: 500 },
    { label: "semibold", weight: 600 },
] satisfies ReadonlyArray<{ label: string; weight: number }>;

const fontGroups = [
    {
        label: "Font family",
        tokens: [
            { name: "--font-family-base", sample: "fontFamily" },
            { name: "--font-family-tight", sample: "fontFamily" },
            {
                name: "--font-family-mono",
                sample: "fontFamily",
                isNotInFigma: true,
            },
        ],
    },
    {
        label: "Font size",
        tokens: textScaleSteps.map((step): CssTokenEntry => ({
            name: `--font-size-${step.size}`,
            sample: "fontSize",
        })),
    },
    {
        label: "Line height",
        tokens: textScaleSteps.map((step): CssTokenEntry => ({
            name: `--font-height-${step.size}`,
            sample: "lineHeight",
        })),
    },
    {
        label: "Font weight",
        tokens: [
            { name: "--font-weight-300", sample: "fontWeight" },
            { name: "--font-weight-400", sample: "fontWeight" },
            { name: "--font-weight-500", sample: "fontWeight" },
            { name: "--font-weight-600", sample: "fontWeight" },
        ],
    },
    {
        // Code-only family: Figma keeps letter-spacing inside its text styles
        // as per-size percentages, with no tracking variables.
        label: "Tracking",
        tokens: [
            { name: "--tracking-base", sample: "tracking", isNotInFigma: true },
        ],
    },
    {
        label: "Deviations",
        tokens: [
            {
                name: "--font-size-11",
                sample: "fontSize",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--font-size-13",
                sample: "fontSize",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--font-size-15",
                sample: "fontSize",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--font-size-22",
                sample: "fontSize",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--font-weight-100",
                sample: "fontWeight",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--tracking-n0-07px",
                sample: "tracking",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--tracking-0-04em",
                sample: "tracking",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--tracking-n0-04em",
                sample: "tracking",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--tracking-0-05em",
                sample: "tracking",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--tracking-0-5px",
                sample: "tracking",
                isDeviation: true,
                isNotInFigma: true,
            },
            {
                name: "--tracking-n0-5px",
                sample: "tracking",
                isDeviation: true,
                isNotInFigma: true,
            },
        ],
    },
] satisfies readonly CssTokenGroup[];

const textScaleMixin = ({
    step,
    weightLabel,
    weight,
}: {
    step: TextScaleStep;
    weightLabel: string;
    weight: number;
}): TypographyMixinEntry => ({
    name: `text-${step.size}-${step.px}-${weightLabel}`,
    copyValue: `@include tokens.text-${step.size}-${step.px}-${weightLabel};`,
    sampleClass: `typo_${step.size}_${weight}`,
    sourceTokens: [
        "--font-family-base",
        `--font-size-${step.size}`,
        `--font-weight-${weight}`,
        `--font-height-${step.size}`,
    ],
    recipeDetail: `letter-spacing: ${step.tracking}`,
});

const figmaScaleMixins = textScaleSteps.flatMap((step) =>
    textWeights.map(({ label, weight }) =>
        textScaleMixin({ step, weightLabel: label, weight }),
    ),
);

const underlineStepSizes = ["2xs", "xs", "s", "m", "l"];

// Figma's "(U)" styles: underlined regular variants.
const figmaUnderlineMixins = textScaleSteps
    .filter((step) => underlineStepSizes.includes(step.size))
    .map((step): TypographyMixinEntry => ({
        name: `text-${step.size}-${step.px}-regular-underline`,
        copyValue: `@include tokens.text-${step.size}-${step.px}-regular-underline;`,
        sampleClass: `typo_${step.size}_underline`,
        sourceTokens: [
            "--font-family-base",
            `--font-size-${step.size}`,
            "--font-weight-400",
            `--font-height-${step.size}`,
        ],
        recipeDetail: `letter-spacing: ${step.tracking}; text-decoration: underline`,
    }));

const typographyGroups = [
    {
        label: "Figma scale",
        mixins: figmaScaleMixins,
    },
    {
        label: "Figma underline",
        mixins: figmaUnderlineMixins,
    },
    {
        // App recipes mined from the codebase, no Figma counterparts. The
        // Figma size-ramp styles fix line-height and use different tracking.
        label: "App recipes",
        mixins: [
            {
                name: "text-title",
                copyValue: "@include tokens.text-title;",
                sampleClass: "textTitle",
                sourceTokens: [
                    "--font-family-base",
                    "--font-size-m",
                    "--font-weight-600",
                    "--tracking-base",
                ],
                isNotInFigma: true,
            },
            {
                name: "text-subtitle",
                copyValue: "@include tokens.text-subtitle;",
                sampleClass: "textSubtitle",
                sourceTokens: [
                    "--font-family-base",
                    "--font-size-l",
                    "--font-weight-500",
                    "--tracking-base",
                ],
                isNotInFigma: true,
            },
            {
                name: "text-heading-1",
                copyValue: "@include tokens.text-heading-1;",
                sampleClass: "textHeading1",
                sourceTokens: [
                    "--font-family-base",
                    "--font-size-4xl",
                    "--font-weight-600",
                ],
                recipeDetail: "letter-spacing: -0.03em",
                isNotInFigma: true,
            },
            {
                name: "text-heading-2",
                copyValue: "@include tokens.text-heading-2;",
                sampleClass: "textHeading2",
                sourceTokens: [
                    "--font-family-base",
                    "--font-size-3xl",
                    "--font-weight-600",
                ],
                recipeDetail: "letter-spacing: -0.03em",
                isNotInFigma: true,
            },
            {
                name: "text-heading-3",
                copyValue: "@include tokens.text-heading-3;",
                sampleClass: "textHeading3",
                sourceTokens: [
                    "--font-family-base",
                    "--font-size-2xl",
                    "--font-weight-600",
                ],
                recipeDetail: "letter-spacing: -0.025em",
                isNotInFigma: true,
            },
        ],
    },
] satisfies readonly TypographyMixinGroup[];

export const designTokenTabs = [
    {
        id: "primitive-colors",
        label: "Primitive colors",
        groups: primitiveColorGroups,
    },
    {
        id: "semantic-colors",
        label: "Semantic colors",
        groups: semanticColorGroups,
    },
    { id: "spacing", label: "Spacing", groups: spacingGroups },
    {
        id: "border-widths",
        label: "Border widths",
        groups: borderWidthGroups,
    },
    { id: "radius", label: "Radius", groups: radiusGroups },
    { id: "shadows", label: "Shadows", groups: shadowGroups },
    { id: "motion", label: "Motion", groups: motionGroups },
    { id: "font", label: "Font", groups: fontGroups },
    { id: "typography", label: "Typography", typographyGroups },
] satisfies readonly DesignTokenTab[];

/** A token row in another tab that the current row is composed from. */
export interface TokenSourceReference {
    tabId: DesignTokenTabId;
    tokenName: `--${string}`;
}

/** Stable DOM id so a row can send focus to one of its source rows. */
export const tokenCopyButtonId = (tokenName: `--${string}`) =>
    `design-token-copy${tokenName}`;
